import { FenError } from '../notation.ts';
import { WALL } from '../pieces.ts';
import type { MoveContext } from '../rules.ts';
import type { Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 8 (draft). The floor is lava. After every 5th move (counting both sides), a random empty
// square turns to lava for good: nothing can land on it or slide across it. Lava is a neutral
// wall piece. "Random" comes from the position itself (a hash of the board), so every game
// gets its own lava, yet both players, the bot and replays always agree.
//
// ext: [moves since the last lava (0–4)]

/** Moves (plies) between lava squares. */
export const LAVA_EVERY = 5;
const COUNT = 0;

export const LAVA_FEN = `${STANDARD_FEN} 0`;

/** Moves until the next lava square appears (1 = right after the next move). */
export function lavaIn(pos: { ext: Int32Array | null }): number {
  return LAVA_EVERY - (pos.ext?.[COUNT] ?? 0);
}

/** The empty square that turns to lava on this board: a hash of the board picks one. */
export function lavaSquare(board: Int8Array, salt: number): number {
  let h = 0x811c9dc5 ^ salt;
  let empty = 0;
  for (let sq = 0; sq < 64; sq++) {
    h = Math.imul(h ^ (board[sq] & 0xff), 0x01000193);
    if (board[sq] === 0) empty++;
  }
  if (!empty) return -1;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  let pick = (h >>> 0) % empty;
  for (let sq = 0; sq < 64; sq++) {
    if (board[sq] !== 0) continue;
    if (pick-- === 0) return sq;
  }
  return -1;
}

export const lava: Variant = {
  id: 'lava',
  info: {
    name: 'Lava Chess',
    but: 'the floor is lava',
    tagline: 'Every few moves another square turns to lava. Nothing gets across it.',
    summary: 'After every 5th move a random empty square **turns to lava** for good. Nothing can land on it or slide across it.',
    rules: [
      'After every 5th move (counting both players), a random empty square turns to lava.',
      'Lava stays for the rest of the game. No piece can land on it or move across it; knights can still jump over.',
      'Lava never appears under a piece.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#ff5a5f',
    emoji: '🌋',
    bots: ['Ember', 'Hot Coals', 'Magma', 'Eruption', 'Volcano'],
  },
  startFen: LAVA_FEN,
  extraPieces: [WALL],
  extSize: 1,
  // No dead-position shortcut: lava can box a king in, so a lone minor piece may still mate.

  parseExtra(fields, pos) {
    const [count = '0'] = fields;
    const n = Number(count);
    if (!Number.isInteger(n) || n < 0 || n >= LAVA_EVERY) throw new FenError('Bad lava counter');
    pos.ext![COUNT] = n;
  },

  formatExtra(pos) {
    return [String(pos.ext![COUNT])];
  },

  afterMove(ctx: MoveContext) {
    const { next } = ctx;
    const ext = next.ext!;
    ext[COUNT] = (ext[COUNT] + 1) % LAVA_EVERY;
    if (ext[COUNT] !== 0) return;
    const sq = lavaSquare(next.board, next.fullmove * 2 + ctx.us);
    if (sq < 0) return;
    ctx.place(sq, WALL, 'lava');
  },
};
